import React, { useState, useEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import moment from 'moment-timezone';
import InputBox from './InputBox';
import { __ } from '../../functions/commom';
import { ReactComponent as LeftIcon } from '../../assets/icons/left.svg';
import { ReactComponent as RightIcon } from '../../assets/icons/right.svg';
import { ReactComponent as DoubleLeftIcon } from '../../assets/icons/double-left.svg';
import { ReactComponent as DoubleRightIcon } from '../../assets/icons/double-right.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar.svg';
import { DATE_FORMAT } from '../../config';

interface DateSelectorProps {
  label?: string | undefined;
  value: string | null;
  onChange: (date: string | null) => void;
  minDate?: string | null;
  maxDate?: string | null;
  defaultDisplayDate?: string | null; // Date to show in calendar when no value is selected
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  errorMessage?: string;
  className?: string;
}

const useStyles = createUseStyles({
  datePickerContainer: {
    position: 'relative',
    width: '100%',
  },
  label: {
    display: 'block',
    marginBottom: '10px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#000000',
    '& .required': {
      color: 'red',
      marginLeft: '3px',
    },
  },
  calendarDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    zIndex: 10,
    width: '300px',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    marginTop: '5px',
  },
  calendarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px',
    borderBottom: '1px solid #eee',
  },
  monthYearText: {
    fontWeight: 'bold',
    fontSize: '16px',
  },
  navButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    color: 'var(--dai-primary-color)',
    padding: '5px 10px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      width: '12px',
      height: '12px',
    },
    '&:hover': {
      backgroundColor: '#f0f0f0',
      '& svg': {
        fill: 'var(--dai-primary-color)',
      },
    },
    '&:disabled': {
      opacity: 0.2,
      color: '#ccc',
      cursor: 'not-allowed',
    },
  },
  weekdaysRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    textAlign: 'center',
    fontWeight: 'bold',
    padding: '10px 0',
    borderBottom: '1px solid #eee',
    fontSize: '12px',
  },
  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '1px',
    padding: '5px',
  },
  dayCell: {
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: '50%',
    margin: '2px',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
    '&.selected': {
      backgroundColor: 'var(--dai-primary-color)',
      color: '#fff',
    },
    '&.disabled': {
      color: '#ccc',
      cursor: 'not-allowed',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    '&.today': {
      border: '1px solid var(--dai-primary-color)',
    },
    '&.outside-month': {
      color: '#aaa',
    },
  },
});

const DateSelector: React.FC<DateSelectorProps> = ({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  defaultDisplayDate,
  disabled = false,
  placeholder = __('Select date'),
  required = false,
  errorMessage,
  className,
}) => {
  const classes = useStyles();
  const [inputValue, setInputValue] = useState<string>('');
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  // Initialize currentMonth: prefer defaultDisplayDate, then maxDate, then minDate, then today
  const getInitialMonth = () => {
    if (defaultDisplayDate && moment(defaultDisplayDate).isValid()) {
      return moment(defaultDisplayDate);
    }
    if (maxDate && moment(maxDate).isValid()) {
      return moment(maxDate);
    }
    if (minDate && moment(minDate).isValid()) {
      return moment(minDate);
    }
    return moment();
  };
  const [currentMonth, setCurrentMonth] = useState<moment.Moment>(getInitialMonth());
  const [localErrorMessage, setLocalErrorMessage] = useState<
    string | undefined
  >(errorMessage);
  const [valueChanged, setValueChanged] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Update input value when selectedDate changes
  useEffect(() => {
    if (value && moment(value).isValid()) {
      setInputValue(moment(value).format('YYYY-MM-DD'));
      setCurrentMonth(moment(value));
    } else {
      setInputValue('');
      // When value is empty, default to defaultDisplayDate, maxDate, or minDate
      if (defaultDisplayDate && moment(defaultDisplayDate).isValid()) {
        setCurrentMonth(moment(defaultDisplayDate));
      } else if (maxDate && moment(maxDate).isValid()) {
        setCurrentMonth(moment(maxDate));
      } else if (minDate && moment(minDate).isValid()) {
        setCurrentMonth(moment(minDate));
      }
    }
  }, [value, minDate, maxDate, defaultDisplayDate]);

  // Update local error message when errorMessage prop changes
  useEffect(() => {
    setLocalErrorMessage(errorMessage);
    setValueChanged(false);
  }, [errorMessage]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setValueChanged(true);

    // Parse the date if the input matches the expected format
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const parsedDate = moment(value, DATE_FORMAT);

      if (parsedDate.isValid()) {
        // Check if date is within allowed range
        if (
          (!minDate ||
            !parsedDate.isBefore(moment(minDate).format(DATE_FORMAT))) &&
          (!maxDate || !parsedDate.isAfter(moment(maxDate).format(DATE_FORMAT)))
        ) {
          onChange(parsedDate.format(DATE_FORMAT));
          setCurrentMonth(parsedDate);
        } else {
          // Date is out of range
          onChange(null);
        }
      } else {
        // Invalid date format
        onChange(null);
      }
    } else if (value === '') {
      // Clear the date if input is empty
      onChange(null);
    }
  };

  const handleInputBoxChange = (value: string | undefined) => {
    // Create a synthetic event object to pass to handleInputChange
    const syntheticEvent = {
      target: { value },
    } as React.ChangeEvent<HTMLInputElement>;

    handleInputChange(syntheticEvent);
  };

  const handleInputFocus = () => {
    setShowCalendar(true);
  };

  const handleInputBlur = () => {
    if (valueChanged && localErrorMessage) {
      setLocalErrorMessage(undefined);
    }
  };

  const handleDateClick = (date: moment.Moment) => {
    onChange(date.format(DATE_FORMAT));
    setShowCalendar(false);
    setValueChanged(true);
    if (localErrorMessage) {
      setLocalErrorMessage(undefined);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth((prevMonth) =>
      direction === 'prev'
        ? moment(prevMonth).subtract(1, 'month')
        : moment(prevMonth).add(1, 'month')
    );
  };

  const navigateYear = (direction: 'prev' | 'next') => {
    setCurrentMonth((prevMonth) =>
      direction === 'prev'
        ? moment(prevMonth).subtract(1, 'year')
        : moment(prevMonth).add(1, 'year')
    );
  };

  const renderCalendar = () => {
    const monthStart = moment(currentMonth).startOf('month');

    // Get the day of the week for the first day of the month (0 = Sunday, 6 = Saturday)
    const startDay = monthStart.day();

    // Calculate the first day to show in the calendar (may be from previous month)
    const firstDayToShow = moment(monthStart).subtract(startDay, 'days');

    const days = [];
    const today = moment().startOf('day');

    // Generate 42 days (6 weeks) to ensure we always show a complete calendar
    for (let i = 0; i < 42; i++) {
      const day = moment(firstDayToShow).add(i, 'days');
      const isCurrentMonth = day.month() === currentMonth.month();
      const isSelected =
        value &&
        day.date() === moment(value).date() &&
        day.month() === moment(value).month() &&
        day.year() === moment(value).year();
      const isToday = day.isSame(today, 'day');
      const isDisabled =
        (minDate && day.isBefore(moment(minDate).format(DATE_FORMAT))) ||
        (maxDate && day.isAfter(moment(maxDate).format(DATE_FORMAT)));

      days.push(
        <div
          key={i}
          className={`${classes.dayCell} ${isSelected ? 'selected' : ''} ${
            !isCurrentMonth ? 'outside-month' : ''
          } ${isToday ? 'today' : ''} ${isDisabled ? 'disabled' : ''}`}
          onClick={() => !isDisabled && handleDateClick(day)}
        >
          {day.date()}
        </div>
      );
    }

    return (
      <div className={classes.calendarDropdown} ref={calendarRef}>
        <div className={classes.calendarHeader}>
          <button
            className={classes.navButton}
            onClick={() => navigateYear('prev')}
            disabled={
              !!minDate &&
              moment(currentMonth)
                .subtract(1, 'year')
                .isBefore(moment(minDate).subtract(1, 'year'))
            }
          >
            <DoubleLeftIcon />
          </button>
          <button
            className={classes.navButton}
            onClick={() => navigateMonth('prev')}
            disabled={
              !!minDate &&
              moment(currentMonth)
                .subtract(1, 'month')
                .isBefore(moment(minDate).subtract(1, 'month'))
            }
          >
            <LeftIcon />
          </button>
          <div className={classes.monthYearText}>
            {__(currentMonth.format('MMMM'))} {currentMonth.format('YYYY')}
          </div>
          <button
            className={classes.navButton}
            onClick={() => navigateMonth('next')}
            disabled={
              !!maxDate &&
              moment(currentMonth)
                .add(1, 'month')
                .isAfter(moment(maxDate).format(DATE_FORMAT))
            }
          >
            <RightIcon />
          </button>
          <button
            className={classes.navButton}
            onClick={() => navigateYear('next')}
            disabled={
              !!maxDate &&
              moment(currentMonth)
                .add(1, 'year')
                .isAfter(moment(maxDate).format(DATE_FORMAT))
            }
          >
            <DoubleRightIcon />
          </button>
        </div>
        <div className={classes.weekdaysRow}>
          <div>{__('Su')}</div>
          <div>{__('Mo')}</div>
          <div>{__('Tu')}</div>
          <div>{__('We')}</div>
          <div>{__('Th')}</div>
          <div>{__('Fr')}</div>
          <div>Sa</div>
        </div>
        <div className={classes.daysGrid}>{days}</div>
      </div>
    );
  };

  return (
    <div
      className={`${classes.datePickerContainer} ${className || ''}`}
      ref={containerRef}
    >
      <InputBox
        ref={inputRef}
        label={label || ''}
        value={inputValue}
        onChange={handleInputBoxChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        type="text"
        placeholder={placeholder}
        errorMessage={localErrorMessage}
        disabled={disabled}
        required={required}
        icon={<CalendarIcon />}
      />
      {showCalendar && !disabled && renderCalendar()}
    </div>
  );
};

export default DateSelector;
